import '@archetype-themes/scripts/config';
import '@archetype-themes/scripts/modules/cart-api';
import '@archetype-themes/scripts/modules/quantity-selectors';
import '@archetype-themes/scripts/helpers/currency';

/*============================================================================
  CartForm
  - Prevent checkout when terms checkbox exists
  - Listen to quantity changes, rebuild cart (both widget and page)
  - CT: debounced quantity updates, ajax remove, inline error messages,
        opgetelde was-prijs en bespaar-badge naast het subtotaal, spinner op
        de regel die wordt bijgewerkt
  - CT: upsell in de drawer via een cascade:
        merkregel (collectie upsell-<vendor>) -> S&D complementary ->
        S&D related -> fallback-collectie. Zie updateRecommendations().
        De rij loopt door (oneindig vegen); zie _setupUpsellRow().
  - CT: upsell-meting (13-10). Elk upsell-blok krijgt de stap uit de cascade
        als data-ct-upsell-source (brand-rule / complementary / related /
        fallback). De Toevoegen-knop stuurt die mee als verborgen
        regel-property _ct_source = "cart-upsell/<bron>". Properties met een
        underscore zijn onzichtbaar in de drawer (cart-item.liquid) en in de
        checkout, maar blijven op de orderregel staan; zo is per order exact
        te zien welke regels via de upsell kwamen en uit welke bron.
        Bijwerking: Shopify voegt regels alleen samen bij gelijke properties,
        dus hetzelfde product later via de PDP toevoegen geeft een tweede
        regel. Zeldzaam, want de rij toont nooit producten die al in de mand
        zitten.
  - CT: de sleep-interactie geldt voor ELKE .ct-drag-row in de drawer, dus
        ook voor de merkenrij in de lege winkelmand
        (snippets/ct-cart-empty.liquid) en de cadeauladder. Bewust één
        handler, geen tweede slider: hij hangt aan this.wrapper, de
        gemeenschappelijke ouder van het formulier en het lege-cart-blok.
  - CT: cadeauladder. Na elke verse markup vuurt cartMarkup() het event
        'cart:markup' met het verse .cart__items-element. assets/ct-gift-ladder.js
        rekent daarop; deze module weet verder niets van de ladder.

  Performance-afspraken voor de drawer:
  - Geen enkele fetch bij het laden van de pagina. De cart-markup staat
    server-side in de drawer (snippets/ct-cart-items.liquid); buildCart()
    draait pas na een mutatie.
  - ÉÉN round trip per mutatie (CT, Section Rendering API). Elke aanroep
    naar /cart/add.js, /cart/change.js en (sinds 29-9) /cart/update.js voor
    kortingscodes vraagt sections/ct-cart-ajax.liquid mee
    (theme.cart.sectionParams()). Het antwoord bevat dan de verse markup,
    gerenderd ná de mutatie, en die gaat via applySections() direct de DOM
    in. De tweede request naar /cart?view=ajax (buildCart) is daarmee alleen
    nog de fallback: als een antwoord geen sections bevat, bij
    pageshow/bfcache en bij cart:build.
  - Events van buitenaf: 'cart:build' (volledige herbouw) en 'cart:sections'
    (detail.sections = het sections-object uit een cart-antwoord; wordt
    toegepast, en alleen bij ontbreken volgt een herbouw). De kortingsmodule
    (assets/ct-cart-discount.js) gebruikt het tweede.

  Snelheid en stabiliteit bij het wijzigen van een aantal (CT):
  - De regel blijft bedienbaar en op volle sterkte; je kunt meteen nog een
    keer klikken. De spinner komt pas in beeld als de wijziging zelf langer
    dan spinnerDelay duurt.
  - Mutaties worden GESERIALISEERD (_enqueue). Drie regels snel achter elkaar
    wijzigen stuurde eerder drie gelijktijdige POSTs naar /cart/change; de
    Shopify cart-API verdraagt dat niet en wijzigingen gingen verloren.
  - De itemlijst wordt alleen nog volledig vervangen als de samenstelling
    verandert (regel erbij, eraf, cadeau erbij). Verandert alleen een aantal,
    dan worden de bedragen ter plekke bijgewerkt. Anders verdween de knop
    waar je net op klikte uit de DOM, ging die klik verloren en sloot de
    drawer zelfs.
  - Snel achter elkaar klikken (24-9): per regel wordt het GEWENSTE aantal
    bijgehouden (desiredQty). Een regel blijft 'dirty' tot de laatste
    wijziging bevestigd is, niet tot de eerste. Anders zette het antwoord
    op een oudere wijziging het getal op het scherm terug (5 -> 3 -> 6) en
    telde de volgende klik vanaf dat oude getal verder.

  EasyGift (tijdelijk, tot de eigen gift-engine er is): de app voegt zijn
  cadeau pas toe NÁ ons antwoord, via zijn eigen interceptie van de
  cart-call. De sections-markup kent dat cadeau dus nog niet. Zolang
  window.EasyGift bestaat volgt daarom na elke mutatie één uitgestelde
  rebuild (scheduleGiftRebuild). Verdwijnt de app, dan verdwijnt die
  request vanzelf mee.

  Teksten: alle zichtbare tekst komt uit de locale files (ct.cart.*) of uit
  Liquid-gerenderde markup: de foutmelding via data-error-text op
  [data-cart-error], het bespaar-label staat als eigen span in de badge en
  alleen het bedrag ([data-cart-savings-amount]) wordt hier vervangen. Zo
  staat er geen talenlijst in de JS.
==============================================================================*/
export default class CartForm {
  constructor(form) {
    this.selectors = {
      products: '[data-products]',
      qtySelector: '.js-qty__wrapper',
      qtyInput: '.js-qty__num',
      discounts: '[data-discounts]',
      subTotal: '[data-subtotal]',
      compareTotal: '[data-cart-compare]',
      /*
        Bespaar-badge in de footer. Bewust een EIGEN attribuutnaam en niet
        data-cart-savings: dat attribuut staat al op .cart__items (het
        opgetelde bedrag uit ct-cart-items.liquid, uitgelezen in
        cartMarkup()) en die staat in de DOM vóór de footer. querySelector
        pakte daardoor de itemlijst in plaats van de badge, het bedrag werd
        nooit ververst en de show/hide-toggle landde op het verkeerde
        element (bug gevonden 28-9). Markup: snippets/header-cart-drawer.liquid.
      */
      saveBadge: '[data-cart-save-badge]',
      saveAmount: '[data-cart-savings-amount]',
      cartCount: '[data-cart-count]',
      lineItem: '.cart__item',
      cartItems: '.cart__items',
      linePrice: '.ct-price-now',
      linePriceWas: '.ct-price-was',
      linePriceBlock: '.cart__item-price',

      cartBubble: '.cart-link__bubble',
      cartNote: '[name="note"]',
      termsCheckbox: '.cart__terms-checkbox',
      checkoutBtn: '.cart__checkout',
      errorMessage: '[data-cart-error]',
      removeBtn: '[data-cart-remove]',
      upsell: '[data-cart-upsell]',
      upsellRow: '.ct-upsell-row',
      upsellItem: '.ct-upsell-item',
      upsellAddBtn: '[data-ct-upsell-add]',
      upsellSource: '[data-ct-upsell-source]',
      // Elke sleepbare rij in de drawer: upsell, merken (lege winkelmand), ladder
      dragRow: '.ct-drag-row',
      dragItem: '.ct-upsell-item, .ct-brand-card, .ct-ladder__tier'
    };

    this.classes = {
      btnLoading: 'btn--loading',
      lineLoading: 'is-loading',
      lineRemoving: 'is-removing',
      dragging: 'is-dragging',
      hidden: 'hide'
    };

    this.config = {
      requiresTerms: false,
      quantityDebounce: 200,
      spinnerDelay: 500,
      giftRebuildDelay: 600,
      upsellFetchLimit: 6,
      upsellShowLimit: 4,
      upsellFallbackSection: 'ct-cart-upsell-fallback',
      // Merkregel: collectie-handle = dit voorvoegsel + vendor (handleize)
      upsellRulePrefix: 'upsell-',
      // Meting: waarde van _ct_source = dit voorvoegsel + bron uit de cascade
      upsellSourcePrefix: 'cart-upsell/',
      dragThreshold: 5
    };

    if (!form) {
      return;
    }

    this.form = form;
    this.wrapper = form.parentNode;
    this.location = form.dataset.location;
    this.namespace = '.cart-' + this.location;
    this.products = form.querySelector(this.selectors.products)
    this.submitBtn = form.querySelector(this.selectors.checkoutBtn);

    this.discounts = form.querySelector(this.selectors.discounts);
    this.subtotal = form.querySelector(this.selectors.subTotal);
    this.compareTotal = form.querySelector(this.selectors.compareTotal);
    this.saveBadge = form.querySelector(this.selectors.saveBadge);
    this.saveAmount = this.saveBadge ? this.saveBadge.querySelector(this.selectors.saveAmount) : null;
    this.termsCheckbox = form.querySelector(this.selectors.termsCheckbox);
    this.noteInput = form.querySelector(this.selectors.cartNote);
    this.errorMessage = form.querySelector(this.selectors.errorMessage);

    this.upsellHolder = form.querySelector(this.selectors.upsell);
    this.upsellFallbackPromise = null;
    this.upsellKey = null;
    this.upsellCache = {};
    this.upsellRuleCache = {};

    this.buildToken = 0;
    this.pending = 0;
    this.queuedSubmit = false;
    this.mutationChain = Promise.resolve();
    this.committedQty = {};
    this.dirtyKeys = {};
    this.desiredQty = {};
    this.quantityTimers = {};
    this.spinnerTimers = {};
    this.giftRebuildTimer = null;

    if (this.termsCheckbox) {
      this.config.requiresTerms = true;
    }

    this.init();
    this._snapshotQuantities();

    /*
      Slepen en pijltjestoetsen op wrapper-niveau: die bevat zowel het
      formulier (met de upsell-rij) als het lege-cart-blok (met de
      merkenrij). De handlers zoeken zelf de dichtstbijzijnde .ct-drag-row,
      dus één binding dekt beide en blijft werken als de rijen vervangen
      worden.
    */
    if (this.wrapper) {
      this._initUpsellDrag(this.wrapper);
      this._initUpsellKeyboard(this.wrapper);
    }

    if (this.upsellHolder) {
      this._setupUpsellRow();
    }
  }

  init() {
    this.initQtySelectors();

    document.addEventListener('cart:quantity' + this.namespace, this.quantityChanged.bind(this));

    // Ajax remove. Delegated on the form so it survives cart rebuilds
    this.form.addEventListener('click', function(evt) {
      const removeBtn = evt.target.closest(this.selectors.removeBtn);
      if (!removeBtn) {
        return;
      }
      evt.preventDefault();
      const key = removeBtn.dataset.key;
      if (!key) {
        return;
      }
      this._removeLine(key, removeBtn);
    }.bind(this));

    /*
      CT: Toevoegen-knop in de upsell-rij. Delegated, dus overleeft het
      vervangen van de rij én werkt op de gekloonde kaarten van de doorlopende
      rij. Loopt via dezelfde wachtrij als de aantalwijzigingen, zodat er nooit
      twee cart-mutaties tegelijk lopen. Vraagt de cart-sectie mee, zodat de
      markup in hetzelfde antwoord zit. Stuurt de bron mee als _ct_source
      (zie de kop van dit bestand).
    */
    this.form.addEventListener('click', function(evt) {
      const addBtn = evt.target.closest(this.selectors.upsellAddBtn);
      if (!addBtn || addBtn.classList.contains(this.classes.btnLoading)) {
        return;
      }
      evt.preventDefault();
      const variantId = addBtn.dataset.variantId;
      if (!variantId) {
        return;
      }

      const sourceEl = addBtn.closest(this.selectors.upsellSource);
      const source = this.config.upsellSourcePrefix + ((sourceEl && sourceEl.dataset.ctUpsellSource) || 'unknown');

      addBtn.classList.add(this.classes.btnLoading);
      this.hideError();
      this._setPending(1);

      this._enqueue(function() {
        const payload = Object.assign({
          items: [{ id: variantId, quantity: 1, properties: { _ct_source: source } }]
        }, theme.cart.sectionParams());

        return fetch(theme.routes.cartAdd, {
          method: 'POST',
          body: JSON.stringify(payload),
          credentials: 'same-origin',
          headers: {
            'Content-Type': 'application/json'
          }
        })
        .then(function(response) { return response.json(); })
        .then(function(data) {
          addBtn.classList.remove(this.classes.btnLoading);
          if (data.status === 422 || data.status === 'bad_request') {
            this.showError(data.description || data.message);
            return;
          }

          const applied = this.applySections(data.sections);
          if (applied) {
            this.scheduleGiftRebuild();
          }

          document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
            detail: {
              product: data,
              addToCartBtn: addBtn,
              sections: data.sections || null,
              // De drawer hoeft niet nog eens te bouwen: de markup staat er al
              handled: applied
            }
          }));
        }.bind(this))
        .catch(function() {
          addBtn.classList.remove(this.classes.btnLoading);
          this.showError();
        }.bind(this));
      }.bind(this))
      .then(function() {
        this._setPending(-1);
      }.bind(this));
    }.bind(this));

    this.form.on('submit' + this.namespace, this.onSubmit.bind(this));

    if (this.noteInput) {
      this.noteInput.addEventListener('change', function() {
        const newNote = this.value;
        theme.cart.updateNote(newNote);
      });
    }

    // Dev-friendly way to build the cart
    document.addEventListener('cart:build', function() {
      this.buildCart();
    }.bind(this));

    /*
      CT: markup uit een cart-antwoord van een andere module (nu: de
      kortingscode). Toepassen; alleen als het antwoord geen bruikbare
      sectie bevatte volgt een volledige herbouw. EasyGift-vangnet net als
      bij de eigen mutaties.
    */
    document.addEventListener('cart:sections', function(evt) {
      const sections = evt.detail ? evt.detail.sections : null;
      if (this.applySections(sections)) {
        this.scheduleGiftRebuild();
        return;
      }
      this.buildCart();
    }.bind(this));
  }

  reInit() {
    this.initQtySelectors();
  }

  /*============================================================================
    Wachtrij voor cart-mutaties

    De Shopify cart-API verwerkt geen parallelle schrijfacties betrouwbaar:
    drie regels tegelijk wijzigen leverde drie POSTs op waarvan er maar één
    overbleef. Alles wat de cart wijzigt loopt daarom door deze keten, dus
    strikt één voor één. De tweede parameter van .then() vangt ook een
    afgewezen voorganger op, zodat één mislukte actie de keten niet blokkeert.
  ==============================================================================*/
  _enqueue(fn) {
    this.mutationChain = this.mutationChain.then(fn, fn);
    return this.mutationChain;
  }

  /*============================================================================
    Lopende wijzigingen

    De teller schakelt de checkout-knop NIET uit: een knop die van kleur
    verandert en niet reageert voelt kapot. Een klik tijdens een wijziging
    wordt vastgehouden en uitgevoerd zodra de cart bevestigd is.
  ==============================================================================*/
  _setPending(delta) {
    this.pending = Math.max(0, this.pending + delta);

    if (this.pending > 0 || !this.queuedSubmit) {
      return;
    }

    this.queuedSubmit = false;

    // Er staat een foutmelding: niet alsnog doorsturen
    if (this.errorMessage && !this.errorMessage.classList.contains(this.classes.hidden)) {
      if (this.submitBtn) {
        this.submitBtn.classList.remove(this.classes.btnLoading);
      }
      return;
    }

    if (this.submitBtn) {
      // click() i.p.v. form.submit(): alleen zo gaat de naam van de knop
      // (checkout) mee in de POST, en dat is wat Shopify naar de checkout
      // stuurt in plaats van naar de cart-pagina.
      this.submitBtn.click();
    }
  }

  /*============================================================================
    Laatst bevestigde aantallen

    Wordt uit de verse servermarkup gelezen, niet uit het scherm: een regel
    die de klant net heeft opgehoogd staat daar mogelijk al op de nieuwe
    waarde terwijl de server nog de oude kent.
  ==============================================================================*/
  _snapshotQuantities(root) {
    const scope = root || this.form;
    this.committedQty = {};

    scope.querySelectorAll(this.selectors.lineItem).forEach(line => {
      const input = line.querySelector(this.selectors.qtyInput);
      const key = line.dataset.key;
      if (input && key) {
        this.committedQty[key] = input.value;
      }
    });
  }

  _revertQuantity(key) {
    const previous = this.committedQty[key];
    if (previous === undefined) {
      return;
    }

    const input = this.form.querySelector(this.selectors.qtyInput + '[data-id="' + key + '"]');
    if (input) {
      input.value = previous;
    }
  }

  /*
    Een bevestigde wijziging afronden. De regel is pas 'schoon' als er niets
    nieuwers meer onderweg is: geen lopende debounce en geen later gewenst
    aantal. Zo niet, dan blijft hij dirty (het getal op het scherm wint van
    de markup) en blijft de spinner staan tot de laatste wijziging binnen is.
  */
  _settleQuantity(key, qty) {
    if (this.quantityTimers[key]) {
      return;
    }
    if (this.desiredQty[key] !== undefined && this.desiredQty[key] !== qty) {
      return;
    }
    delete this.dirtyKeys[key];
    delete this.desiredQty[key];
    this._stopLineLoading(key);
  }

  // Terugzetten bij een fout, maar niet als er al een nieuwere wijziging loopt
  _revertIfSettled(key) {
    if (!this.dirtyKeys[key]) {
      this._revertQuantity(key);
    }
  }

  /*============================================================================
    Staat tijdens een wijziging

    De spinner komt pas na spinnerDelay in beeld en wordt opgeruimd zodra het
    antwoord van /cart/change binnen is. Verwijderen toont hem direct, want
    daar verdwijnt de regel.
  ==============================================================================*/
  setLineLoading(el, mode) {
    if (!el) {
      return;
    }
    const line = el.closest(this.selectors.lineItem);
    if (!line) {
      return;
    }

    if (mode === 'removing') {
      line.classList.add(this.classes.lineRemoving, this.classes.lineLoading);
      return;
    }

    const key = line.dataset.key;
    clearTimeout(this.spinnerTimers[key]);
    this.spinnerTimers[key] = setTimeout(function() {
      line.classList.add(this.classes.lineLoading);
    }.bind(this), this.config.spinnerDelay);
  }

  _stopLineLoading(key) {
    clearTimeout(this.spinnerTimers[key]);
    delete this.spinnerTimers[key];

    const line = this.products.querySelector(this.selectors.lineItem + '[data-key="' + key + '"]');
    if (line && !line.classList.contains(this.classes.lineRemoving)) {
      line.classList.remove(this.classes.lineLoading);
    }
  }

  _clearLineLoading() {
    Object.keys(this.spinnerTimers).forEach(key => {
      clearTimeout(this.spinnerTimers[key]);
    });
    this.spinnerTimers = {};

    this.form.querySelectorAll(this.selectors.lineItem).forEach(el => {
      el.classList.remove(this.classes.lineLoading, this.classes.lineRemoving);
    });
  }

  /*
    Een regel verwijderen: via de prullenbak of via aantal 0.

    Een nog lopende debounce voor dezelfde regel wordt geannuleerd. Anders
    vuurde die na het verwijderen nog een /cart/change af voor een regel die
    niet meer bestaat, en kreeg de klant een foutmelding te zien.
  */
  _removeLine(key, el) {
    clearTimeout(this.quantityTimers[key]);
    delete this.quantityTimers[key];
    delete this.dirtyKeys[key];
    delete this.desiredQty[key];

    this.setLineLoading(el, 'removing');
    this.commitQuantity(key, 0);
  }

  onSubmit(evt) {
    /*
      Er loopt nog een wijziging. Niet weigeren en niet uitschakelen: de klik
      wordt vastgehouden, de knop toont zijn laadstaat en _setPending() maakt
      hem af zodra de cart bevestigd is.
    */
    if (this.pending > 0) {
      evt.preventDefault();
      this.queuedSubmit = true;
      if (this.submitBtn) {
        this.submitBtn.classList.add(this.classes.btnLoading);
      }
      return false;
    }

    if (this.config.requiresTerms && !this.termsCheckbox.checked) {
      alert(theme.strings.cartTermsConfirmation);
      evt.preventDefault();
      return false;
    }

    if (this.submitBtn) {
      this.submitBtn.classList.add(this.classes.btnLoading);
    }
  }

  /*============================================================================
    Query cart page to get markup
  ==============================================================================*/
  _parseProductHTML(text) {
    if (!text) {
      return { items: null, discounts: null };
    }

    const html = document.createElement('div');
    html.innerHTML = text;

    return {
      items: html.querySelector(this.selectors.cartItems),
      discounts: html.querySelector('.cart__discounts')
    }
  }

  buildCart() {
    const token = ++this.buildToken;
    this._setPending(1);

    return theme.cart.getCartProductMarkup()
      .then(function(text) {
        if (token !== this.buildToken) {
          return;
        }
        this.cartMarkup(text);
      }.bind(this))
      .catch(function() {
        if (token !== this.buildToken) {
          return;
        }
        this._clearLineLoading();
        this._showFallbackError();
      }.bind(this))
      .then(function() {
        this._setPending(-1);
      }.bind(this));
  }

  /*============================================================================
    CT: markup uit de Section Rendering API toepassen.

    `sections` is het object uit het antwoord van /cart/add.js,
    /cart/change.js of /cart/update.js. Bevat het onze sectie, dan is dat de
    complete, verse cart-markup en gaat hij door hetzelfde pad als een
    rebuild. Geeft true terug als er iets is toegepast, zodat de aanroeper
    weet dat een buildCart() overbodig is.
  ==============================================================================*/
  applySections(sections) {
    if (!sections || typeof sections !== 'object') {
      return false;
    }
    const html = sections[theme.cart.sectionId];
    if (!html || html.indexOf(this.selectors.cartItems.slice(1)) === -1) {
      return false;
    }

    // Een oudere rebuild die nog onderweg is mag dit niet meer overschrijven
    this.buildToken++;
    this.cartMarkup(html);
    return true;
  }

  /*
    EasyGift-vangnet: één uitgestelde rebuild per reeks mutaties, alleen
    zolang de app aanwezig is. Zie de toelichting bovenaan dit bestand.
  */
  scheduleGiftRebuild() {
    if (!window.EasyGift) {
      return;
    }
    clearTimeout(this.giftRebuildTimer);
    this.giftRebuildTimer = setTimeout(function() {
      this.giftRebuildTimer = null;
      this.buildCart();
    }.bind(this), this.config.giftRebuildDelay);
  }

  /*
    Fallback wanneer een antwoord GEEN sections bevat: bedragen en tellers
    bijwerken uit de cart-JSON, in afwachting van de rebuild.

    De was-prijs per regel, het opgetelde was-bedrag en de bespaar-badge
    vragen compare_at_price en die zit niet in de cart-JSON. Die worden hier
    daarom VERBORGEN in plaats van bijgewerkt: anders stond er tot de
    rebuild een doorgestreepte prijs die lager is dan de nieuwe prijs (bij
    1 → 2: was €50, nu €80). De rebuild zet ze terug.
  */
  _applyCartJson(cart) {
    if (!cart || !cart.items) {
      return;
    }

    this.updateCount(cart.item_count);

    if (this.subtotal) {
      this.subtotal.innerHTML = theme.Currency.formatMoney(cart.total_price, theme.settings.moneyFormat);
    }
    if (this.compareTotal) {
      this.compareTotal.classList.add(this.classes.hidden);
    }
    if (this.saveBadge) {
      this.saveBadge.classList.add(this.classes.hidden);
    }

    cart.items.forEach(function(item) {
      const line = this.products.querySelector(this.selectors.lineItem + '[data-key="' + item.key + '"]');
      if (!line) {
        return;
      }
      const price = line.querySelector(this.selectors.linePrice);
      if (price) {
        price.innerHTML = theme.Currency.formatMoney(item.final_line_price, theme.settings.moneyFormat);
      }
      const was = line.querySelector(this.selectors.linePriceWas);
      if (was) {
        was.classList.add(this.classes.hidden);
      }
    }.bind(this));
  }

  /*
    Verse markup toepassen.

    Is de samenstelling van de cart gelijk gebleven (zelfde regels in dezelfde
    volgorde), dan worden alleen de prijsblokken en aantallen bijgewerkt. De
    knoppen waar de klant op klikt blijven dan staan.

    Verandert de samenstelling wél (regel erbij of eraf, cadeau dat verschijnt),
    dan is een volledige vervanging nodig én veilig.
  */
  _renderItems(items) {
    const incoming = Array.prototype.slice.call(items.querySelectorAll(this.selectors.lineItem));
    const current = Array.prototype.slice.call(this.products.querySelectorAll(this.selectors.lineItem));

    const incomingKeys = incoming.map(el => el.dataset.key).join('|');
    const currentKeys = current.map(el => el.dataset.key).join('|');

    if (currentKeys && incomingKeys === currentKeys) {
      incoming.forEach(function(newLine, index) {
        const oldLine = current[index];
        const key = newLine.dataset.key;

        const newPrice = newLine.querySelector(this.selectors.linePriceBlock);
        const oldPrice = oldLine.querySelector(this.selectors.linePriceBlock);
        if (newPrice && oldPrice) {
          oldPrice.innerHTML = newPrice.innerHTML;
        }

        /*
          CT (cadeauladder): het regelbedrag reist mee in data-ct-line
          (snippets/cart-item.liquid). Van alle ladder-attributen is dit
          het enige dat bij een aantalwijziging verandert.
        */
        if (newLine.dataset.ctLine) {
          oldLine.dataset.ctLine = newLine.dataset.ctLine;
        }

        /*
          Het aantal alleen overschrijven als er voor deze regel niets meer
          onderweg is. Staat er nog een wijziging open, dan is het getal op
          het scherm nieuwer dan deze markup.
        */
        if (!this.dirtyKeys[key]) {
          const newInput = newLine.querySelector(this.selectors.qtyInput);
          const oldInput = oldLine.querySelector(this.selectors.qtyInput);
          if (newInput && oldInput) {
            oldInput.value = newInput.value;
          }
        }
      }.bind(this));

      return;
    }

    /*
      Focus bewaren over een volledige vervanging heen. Stond de focus op
      een knop in een regel (bijv. + waarna EasyGift een cadeau toevoegde),
      dan verdween die knop uit de DOM en viel de focus terug op body. We
      zoeken dezelfde knop in dezelfde regel terug op data-key.
    */
    const active = document.activeElement;
    let refocus = null;
    if (active && this.products.contains(active)) {
      const line = active.closest(this.selectors.lineItem);
      if (line && line.dataset.key) {
        refocus = { key: line.dataset.key, className: active.className };
      }
    }

    this.products.innerHTML = '';
    this.products.append(items);

    // Nieuwe DOM, dus nieuwe aantal-selectors
    this.reInit();

    /*
      Ook na een volledige vervanging (cadeau erbij tijdens snel klikken)
      wint het gewenste aantal van de servermarkup zolang het nog onderweg is.
    */
    Object.keys(this.dirtyKeys).forEach(function(k) {
      const want = this.desiredQty[k];
      if (want === undefined) {
        return;
      }
      const input = this.products.querySelector(this.selectors.qtyInput + '[data-id="' + k + '"]');
      if (input) {
        input.value = want;
      }
    }.bind(this));

    if (refocus) {
      const newLine = this.products.querySelector(this.selectors.lineItem + '[data-key="' + refocus.key + '"]');
      const target = newLine && Array.prototype.find.call(newLine.querySelectorAll('button, a, input'), function(el) {
        return el.className === refocus.className;
      });
      if (target) {
        try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
      }
    }
  }

  cartMarkup(text) {
    const markup = this._parseProductHTML(text);
    const items = markup.items;

    /*
      Geen bruikbare markup: netwerkfout, een foutpagina van Shopify of een
      onderbroken navigatie. Niets slopen — de bestaande inhoud blijft staan,
      de spinners gaan weg en de klant krijgt een melding.
    */
    if (!items) {
      this._clearLineLoading();
      this._showFallbackError();
      return;
    }

    const count = parseInt(items.dataset.count);
    const subtotal = items.dataset.cartSubtotal;
    const compareTotal = items.dataset.cartCompareTotal;
    const savings = items.dataset.cartSavings;

    this.updateCartDiscounts(markup.discounts);

    if (count > 0) {
      this.wrapper.classList.remove('is-empty');
    } else {
      this.wrapper.classList.add('is-empty');
    }

    this.updateCount(count);

    // Stand van de server vastleggen vóórdat de markup de DOM in gaat
    this._snapshotQuantities(items);

    this._renderItems(items);

    /*
      CT (cadeauladder): `items` is altijd het VERSE element uit de server-
      markup, ook als _renderItems() de bestaande regels heeft laten staan.
      De ladder rekent daarop (data-attributen op .cart__items en .cart__item).
    */
    document.dispatchEvent(new CustomEvent('cart:markup', { detail: { items: items } }));

    if (this.subtotal) {
      this.subtotal.innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
    }

    this.updateComparePricing(compareTotal, savings);

    this.updateRecommendations(items.dataset.recProductId, items.dataset.cartProductIds, items.dataset.recVendor);

    if (Shopify && Shopify.StorefrontExpressButtons) {
      Shopify.StorefrontExpressButtons.initialize();
    }
  }

  /*============================================================================
    CT: upsell-cascade voor [data-cart-upsell] (alleen aanwezig in de drawer;
    op de cart-pagina is dit een no-op).

    1. MERKREGEL (David, 28-9): collectie 'upsell-<vendor>' van het
       basisproduct, opgehaald via sections/ct-cart-upsell-fallback.liquid
       op de collectie-URL. Handmatig gecureerd; mag meerdere merken
       bevatten. Bestaat de collectie niet (de sectie rendert dan leeg) of
       levert hij na filtering niets op, dan door naar 2.
    2. S&D complementary (handmatig gecureerd; voor Dermaceutic gevuld op
       28-9 met elementrē-equivalenten afgewisseld met Dermaceutic)
    3. S&D related (automatisch gegenereerd)
    4. Fallback-collectie via dezelfde sectie op de homepage-URL

    De stap die de rij levert gaat als bron mee naar _showUpsell(), voor de
    meting (zie de kop van dit bestand): brand-rule, complementary, related
    of fallback.

    Bij de merkregel worden de kaarten per merk AFGEWISSELD (_mixByVendor),
    te beginnen met een merk dat niet het merk van het basisproduct is.

    vendor is optioneel: cart-drawer.js (prepareCart) geeft alleen id en
    cart-ids door, dan wordt de vendor uit de itemlijst in de DOM gelezen.

    Fallback en merkregel worden NIET server-side op elke pageview
    gerenderd (dat was een Liquid-loop met een Locksmith-render per product,
    op elke pagina, ook voor bezoekers die de mand nooit openen). Ze worden
    opgehaald zodra ze nodig zijn en per sessie gecachet.
  ==============================================================================*/
  updateRecommendations(productId, cartIdsStr, vendor) {
    if (!this.upsellHolder) {
      return;
    }

    if (vendor === undefined) {
      const items = this.products ? this.products.querySelector(this.selectors.cartItems) : null;
      vendor = items ? items.dataset.recVendor : '';
    }
    vendor = vendor || '';

    const cartIds = (cartIdsStr || '').split(',').filter(Boolean);
    const key = (productId || '') + '|' + vendor + '|' + cartIds.join(',');
    if (key === this.upsellKey) {
      return;
    }
    this.upsellKey = key;

    if (!productId) {
      this.restoreUpsellFallback(cartIds);
      return;
    }

    const rulePromise = vendor ? this._fetchRule(vendor) : Promise.resolve('');

    rulePromise.then(function(ruleHtml) {
      if (key !== this.upsellKey) {
        return;
      }
      const ruleBlock = this._filterUpsell(ruleHtml, cartIds, vendor);
      if (ruleBlock) {
        this._showUpsell(ruleBlock, 'brand-rule');
        return;
      }

      return Promise.all([
        this._fetchUpsell(productId, 'complementary'),
        this._fetchUpsell(productId, 'related')
      ]).then(function(htmls) {
        // Race-guard: alleen toepassen als de cart intussen niet is veranderd
        if (key !== this.upsellKey) {
          return;
        }
        let block = this._filterUpsell(htmls[0], cartIds);
        let source = 'complementary';
        if (!block) {
          block = this._filterUpsell(htmls[1], cartIds);
          source = 'related';
        }
        if (block) {
          this._showUpsell(block, source);
        } else {
          this.restoreUpsellFallback(cartIds);
        }
      }.bind(this));
    }.bind(this));
  }

  _showUpsell(block, source) {
    block.dataset.ctUpsellSource = source || 'unknown';
    this.upsellHolder.innerHTML = '';
    this.upsellHolder.append(block);
    this._setupUpsellRow();
  }

  _fetchUpsell(productId, intent) {
    const cacheKey = productId + ':' + intent;
    if (!this.upsellCache[cacheKey]) {
      const base = theme.routes.productRecommendations || '/recommendations/products';
      const url = base + '?section_id=ct-cart-recommendations&product_id=' + productId +
        '&limit=' + this.config.upsellFetchLimit + '&intent=' + intent;
      this.upsellCache[cacheKey] = fetch(url)
        .then(function(r) { return r.ok ? r.text() : ''; })
        .catch(function() { return ''; });
    }
    return this.upsellCache[cacheKey];
  }

  /*
    Merkregel: de sectie op de collectie-URL. Bestaat de collectie niet,
    dan rendert de sectie leeg (request.page_type-bewaking in de sectie)
    en levert dit '' op na filtering. Gecachet per vendor, ook het lege
    resultaat, zodat een merk zonder regel niet bij elke cartwijziging
    opnieuw wordt opgevraagd.
  */
  _fetchRule(vendor) {
    if (!this.upsellRuleCache[vendor]) {
      let base = theme.routes.collections || '/collections';
      if (base.charAt(base.length - 1) !== '/') {
        base += '/';
      }
      const url = base + this.config.upsellRulePrefix + encodeURIComponent(vendor) +
        '?section_id=' + this.config.upsellFallbackSection;
      this.upsellRuleCache[vendor] = fetch(url)
        .then(function(r) { return r.ok ? r.text() : ''; })
        .catch(function() { return ''; });
    }
    return this.upsellRuleCache[vendor];
  }

  /*
    Kaarten uit een antwoord filteren: producten die al in de mand zitten
    eruit, maximaal upsellShowLimit over. Met `mixVendor` (merkregel) worden
    de overgebleven kaarten eerst per merk afgewisseld.
  */
  _filterUpsell(html, cartIds, mixVendor) {
    if (!html || html.indexOf('ct-upsell-item') === -1) {
      return null;
    }

    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const block = tmp.querySelector('.cart-recommendations');
    if (!block) {
      return null;
    }

    block.querySelectorAll(this.selectors.upsellItem).forEach(function(el) {
      const pid = el.getAttribute('data-product-id');
      if (pid && cartIds.indexOf(pid) !== -1) {
        el.remove();
      }
    });

    if (mixVendor) {
      this._mixByVendor(block, mixVendor);
    }

    let kept = 0;
    const limit = this.config.upsellShowLimit;
    block.querySelectorAll(this.selectors.upsellItem).forEach(function(el) {
      if (kept >= limit) {
        el.remove();
      } else {
        kept++;
      }
    });

    return kept > 0 ? block : null;
  }

  /*
    Kaarten per merk afwisselen (round-robin). Volgorde van de merken: eerst
    de merken die NIET `cartVendor` zijn, in volgorde van eerste voorkomen
    in de collectie, daarna cartVendor zelf. Binnen een merk blijft de
    volgorde van de collectie staan. Met vier zichtbare kaarten en twee
    merken is dat 2/2, openend met het andere merk.
  */
  _mixByVendor(block, cartVendor) {
    const row = block.querySelector(this.selectors.upsellRow);
    if (!row) {
      return;
    }
    const items = Array.prototype.slice.call(row.querySelectorAll(this.selectors.upsellItem));
    if (items.length < 2) {
      return;
    }

    const groups = {};
    const order = [];
    items.forEach(function(el) {
      const v = el.getAttribute('data-vendor') || '';
      if (!groups[v]) {
        groups[v] = [];
        order.push(v);
      }
      groups[v].push(el);
    });
    if (order.length < 2) {
      return;
    }

    const vendors = order.filter(function(v) { return v !== cartVendor; });
    if (groups[cartVendor]) {
      vendors.push(cartVendor);
    }

    const mixed = [];
    let remaining = items.length;
    while (remaining > 0) {
      vendors.forEach(function(v) {
        const next = groups[v].shift();
        if (next) {
          mixed.push(next);
          remaining--;
        }
      });
    }

    mixed.forEach(function(el) {
      row.appendChild(el);
    });
  }

  _fetchFallback() {
    if (!this.upsellFallbackPromise) {
      let base = theme.routes.home || '/';
      if (base.charAt(base.length - 1) !== '/') {
        base += '/';
      }
      this.upsellFallbackPromise = fetch(base + '?section_id=' + this.config.upsellFallbackSection)
        .then(function(r) { return r.ok ? r.text() : ''; })
        .catch(function() { return ''; });
    }
    return this.upsellFallbackPromise;
  }

  restoreUpsellFallback(cartIds) {
    if (!this.upsellHolder) {
      return;
    }
    const key = this.upsellKey;
    this._fetchFallback().then(function(html) {
      // Race-guard: de cart kan intussen veranderd zijn
      if (key !== this.upsellKey) {
        return;
      }
      const block = this._filterUpsell(html, cartIds || []);
      this.upsellHolder.innerHTML = '';
      if (block) {
        block.dataset.ctUpsellSource = 'fallback';
        this.upsellHolder.append(block);
        this._setupUpsellRow();
      }
    }.bind(this));
  }

  /*============================================================================
    CT: doorlopende upsell-rij (oneindig vegen)

    De kaarten worden één keer gedupliceerd en tijdens het scrollen wordt de
    positie teruggezet zodra je een volle ronde verder bent. Je komt daardoor
    nooit tegen een einde aan.

    Waarom een gemeten periode en niet scrollWidth / 2: de rij heeft links een
    padding en tussen de kaarten een gap, dus de helft van de scrollbreedte is
    NIET gelijk aan de afstand van één ronde. Het verschil zou je zien als een
    sprongetje. De periode wordt daarom gemeten als de afstand tussen de eerste
    originele kaart en de eerste kloon.

    Alleen aanzetten als de rij niet in één keer past. Past hij wel, dan is er
    niets te vegen en zouden dubbele kaarten alleen maar verwarren.

    Bewust NIET toegepast op de merkenrij: daar is een duidelijk begin en eind
    juist prettig, en het zijn er te weinig.

    De klonen staan op aria-hidden met tabindex -1: voor een screenreader en
    voor Tab bestaat de rij dus één keer. De Toevoegen-knoppen erop werken
    gewoon, want die worden gedelegeerd afgehandeld.

    De startpositie is 1px in plaats van 0: op 0 vuurt de browser geen
    scroll-event meer als je verder naar links veegt, en dan zou de lus aan
    die kant vastlopen.
  ==============================================================================*/
  _setupUpsellRow() {
    if (!this.upsellHolder) {
      return;
    }

    const row = this.upsellHolder.querySelector(this.selectors.upsellRow);
    if (!row || row.dataset.ctLoop) {
      return;
    }

    const items = Array.prototype.slice.call(row.querySelectorAll(this.selectors.upsellItem));
    if (items.length < 2 || row.scrollWidth <= row.clientWidth + 8) {
      return;
    }

    items.forEach(function(item) {
      const clone = item.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('data-upsell-clone', '');
      clone.querySelectorAll('a, button').forEach(function(el) {
        el.setAttribute('tabindex', '-1');
      });
      row.appendChild(clone);
    });

    const all = row.querySelectorAll(this.selectors.upsellItem);
    const period = all[items.length].offsetLeft - all[0].offsetLeft;
    if (!period || period <= 0) {
      return;
    }

    row.dataset.ctLoop = period;
    row.scrollLeft = 1;

    row.addEventListener('scroll', function() {
      this._wrapUpsellScroll(row);
    }.bind(this), { passive: true });
  }

  _wrapUpsellScroll(row) {
    const period = parseFloat(row.dataset.ctLoop);
    if (!period) {
      return;
    }

    if (row.scrollLeft >= period) {
      row.scrollLeft -= period;
    } else if (row.scrollLeft <= 0) {
      row.scrollLeft = period - 1;
    }
  }

  /*
    CT: slepen met de muis over de HELE rij — ook op afbeeldingen, titels en
    prijzen, niet alleen in de lege ruimte ertussen.

    Geldt voor elke .ct-drag-row binnen root: de upsell-rij, de merkenrij
    in de lege winkelmand en de cadeauladder.

    De verplaatsing is INCREMENTEEL (verschil t.o.v. de vorige muispositie) en
    niet absoluut t.o.v. het beginpunt. Dat is nodig omdat de doorlopende rij
    de scrollpositie tussentijds terugzet; met een absolute berekening zou het
    beeld dan verspringen.

    draggable="false" in de markup voorkomt de native link-drag; de
    dragstart-handler hieronder is het vangnet. Touch wordt met rust gelaten:
    dat scrollt native.
  */
  _initUpsellDrag(root) {
    let row = null;
    let pointerId = null;
    let startX = 0;
    let lastX = 0;
    let dragged = false;

    root.addEventListener('dragstart', function(evt) {
      if (evt.target.closest(this.selectors.dragRow)) {
        evt.preventDefault();
      }
    }.bind(this));

    root.addEventListener('pointerdown', function(evt) {
      if (evt.pointerType !== 'mouse' || evt.button !== 0) {
        return;
      }
      const target = evt.target.closest(this.selectors.dragRow);
      if (!target) {
        return;
      }
      row = target;
      pointerId = evt.pointerId;
      startX = evt.clientX;
      lastX = evt.clientX;
      dragged = false;
    }.bind(this));

    root.addEventListener('pointermove', function(evt) {
      if (!row || evt.pointerId !== pointerId) {
        return;
      }
      if (!dragged) {
        if (Math.abs(evt.clientX - startX) < this.config.dragThreshold) {
          return;
        }
        dragged = true;
        row.classList.add(this.classes.dragging);
        if (row.setPointerCapture) {
          try { row.setPointerCapture(pointerId); } catch (e) { /* genegeerd */ }
        }
      }
      evt.preventDefault();
      row.scrollLeft -= evt.clientX - lastX;
      lastX = evt.clientX;
    }.bind(this));

    const stop = function() {
      if (row) {
        row.classList.remove(this.classes.dragging);
        if (pointerId !== null && row.releasePointerCapture && row.hasPointerCapture && row.hasPointerCapture(pointerId)) {
          try { row.releasePointerCapture(pointerId); } catch (e) { /* genegeerd */ }
        }
      }
      row = null;
      pointerId = null;
      window.setTimeout(function() {
        dragged = false;
      }, 0);
    }.bind(this);

    root.addEventListener('pointerup', stop);
    root.addEventListener('pointercancel', stop);
    window.addEventListener('pointerup', stop);

    // Klik onderdrukken die het gevolg is van slepen (anders opent de
    // productpagina of de merkcollectie zodra je de rij verschuift).
    root.addEventListener('click', function(evt) {
      if (dragged && evt.target.closest(this.selectors.dragRow)) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }.bind(this), true);
  }

  _initUpsellKeyboard(root) {
    root.addEventListener('keydown', function(evt) {
      if (evt.key !== 'ArrowLeft' && evt.key !== 'ArrowRight') {
        return;
      }
      const row = evt.target.closest(this.selectors.dragRow);
      if (!row) {
        return;
      }
      const item = row.querySelector(this.selectors.dragItem);
      const step = item ? item.offsetWidth + 12 : 160;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      row.scrollBy({
        left: evt.key === 'ArrowRight' ? step : -step,
        behavior: reduce ? 'auto' : 'smooth'
      });
      evt.preventDefault();
    }.bind(this));
  }

  /*============================================================================
    Was-prijs en bespaar-badge bij het subtotaal

    Beide bestaan alleen als er iets te besparen valt. In de badge wordt
    alleen het bedrag ([data-cart-savings-amount]) vervangen; het label
    ervoor blijft de Liquid-vertaalde tekst. Met de theme-formatter, dus
    inclusief eventuele superscript-decimalen (innerHTML).
  ==============================================================================*/
  updateComparePricing(compareTotal, savings) {
    const savedAmount = parseInt(savings, 10);
    const hasSavings = !isNaN(savedAmount) && savedAmount > 0;

    if (this.compareTotal) {
      if (hasSavings) {
        this.compareTotal.innerHTML = theme.Currency.formatMoney(compareTotal, theme.settings.moneyFormat);
        this.compareTotal.classList.remove(this.classes.hidden);
      } else {
        this.compareTotal.classList.add(this.classes.hidden);
      }
    }

    if (this.saveBadge) {
      if (hasSavings) {
        if (this.saveAmount) {
          this.saveAmount.innerHTML = theme.Currency.formatMoney(savedAmount, theme.settings.moneyFormat);
        }
        this.saveBadge.classList.remove(this.classes.hidden);
      } else {
        this.saveBadge.classList.add(this.classes.hidden);
      }
    }
  }

  updateCartDiscounts(markup) {
    if (!this.discounts) {
      return;
    }
    this.discounts.innerHTML = '';
    if (markup) {
      this.discounts.append(markup);
    }
  }

  /*============================================================================
    Quantity handling
  ==============================================================================*/
  initQtySelectors() {
    this.form.querySelectorAll(this.selectors.qtySelector).forEach(el => {
      const selector = new theme.QtySelector(el, {
        namespace: this.namespace,
        isCart: true
      });
    });
  }

  quantityChanged(evt) {
    const key = evt.detail[0];
    const qty = parseInt(evt.detail[1], 10);
    const el = evt.detail[2];

    /*
      Bewust parseInt: theme.QtySelector geeft bij 0 de STRING "0" door (de
      waarde van het min-attribuut), en `!qty` zou een echte 0 juist
      weggooien. Nul is een verwijdering en gaat via hetzelfde pad als de
      prullenbak: direct, zonder debounce, met de verwijder-staat.
    */
    if (!key || isNaN(qty)) {
      return;
    }

    if (qty === 0) {
      this._removeLine(key, el);
      return;
    }

    // Deze regel wijkt nu af van de server; markup mag het getal niet
    // overschrijven zolang dat zo is. desiredQty = het laatst gekozen aantal.
    this.dirtyKeys[key] = true;
    this.desiredQty[key] = qty;

    this.setLineLoading(el);

    clearTimeout(this.quantityTimers[key]);
    this.quantityTimers[key] = setTimeout(function() {
      delete this.quantityTimers[key];
      this.commitQuantity(key, qty);
    }.bind(this), this.config.quantityDebounce);
  }

  commitQuantity(key, qty) {
    this.hideError();
    this._setPending(1);

    this._enqueue(function() {
      return theme.cart.changeItem(key, qty)
        .then(function(cart) {
          let parsedCart = null;
          try {
            parsedCart = JSON.parse(cart);
          } catch (e) {
            parsedCart = null;
          }

          this._settleQuantity(key, qty);

          if (!parsedCart) {
            this._revertIfSettled(key);
            this._showFallbackError();
            return false;
          }

          if (parsedCart.status === 422) {
            this._revertIfSettled(key);
            this.showError(parsedCart.message || parsedCart.description);
            document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: parsedCart } }));
            return false;
          }

          /*
            Eerst de sections-markup (één round trip). Ontbreekt die, dan de
            oude route: bedragen uit de JSON en daarna een rebuild.
          */
          const applied = this.applySections(parsedCart.sections);
          if (!applied) {
            if (parsedCart.item_count > 0) {
              this.wrapper.classList.remove('is-empty');
            } else {
              this.wrapper.classList.add('is-empty');
            }
            this._applyCartJson(parsedCart);
          }

          document.dispatchEvent(new CustomEvent('cart:updated', {
            detail: {
              cart: parsedCart
            }
          }));

          return applied;
        }.bind(this))
        .catch(function() {
          this._settleQuantity(key, qty);
          this._revertIfSettled(key);
          this._showFallbackError();
          return false;
        }.bind(this));
    }.bind(this))
    .then(function(applied) {
      if (applied) {
        this.scheduleGiftRebuild();
        return;
      }
      return this.buildCart();
    }.bind(this))
    .then(function() {
      this._setPending(-1);
    }.bind(this));
  }

  /*============================================================================
    Inline error messages (fall back to alert on the cart page)

    De algemene tekst komt uit data-error-text op [data-cart-error], gezet in
    snippets/header-cart-drawer.liquid uit ct.cart.update_failed. Zo staat er
    geen talenlijst in deze module en kan de tekst in de vertaal-editor
    aangepast worden. Op de cart-pagina bestaat dat element niet; daar valt
    het terug op de themestring, zodat een fout nooit stil blijft.

    _showFallbackError() overschrijft een melding die er al staat niet: een
    concrete melding van Shopify ("nog maar 2 op voorraad") is bruikbaarder
    dan de algemene verbindingstekst die er anders vlak daarna overheen zou
    komen vanuit de mislukte rebuild.
  ==============================================================================*/
  showError(message) {
    const fallback = this.errorMessage ? this.errorMessage.dataset.errorText : theme.strings.unavailable;
    const text = message || fallback || '';

    if (!text) {
      return;
    }

    if (this.errorMessage) {
      this.errorMessage.textContent = text;
      this.errorMessage.classList.remove(this.classes.hidden);
    } else {
      alert(text);
    }
  }

  _showFallbackError() {
    if (this.errorMessage && !this.errorMessage.classList.contains(this.classes.hidden)) {
      return;
    }
    this.showError();
  }

  hideError() {
    if (this.errorMessage) {
      this.errorMessage.classList.add(this.classes.hidden);
      this.errorMessage.textContent = '';
    }
  }

  /*============================================================================
    Update elements of the cart
  ==============================================================================*/
  updateSubtotal(subtotal) {
    this.form.querySelector(this.selectors.subTotal).innerHTML = theme.Currency.formatMoney(subtotal, theme.settings.moneyFormat);
  }

  updateCount(count) {
    const countEls = document.querySelectorAll('.cart-link__bubble-num');

    if (countEls.length) {
      countEls.forEach(el => {
        el.innerText = count;
      });
    }

    // Teller in de kop van de drawer: verbergen bij een lege cart
    const drawerCounts = document.querySelectorAll(this.selectors.cartCount);
    if (drawerCounts.length) {
      drawerCounts.forEach(el => {
        el.innerText = count;
        if (count > 0) {
          el.classList.remove(this.classes.hidden);
        } else {
          el.classList.add(this.classes.hidden);
        }
      });
    }

    // show/hide bubble(s)
    const bubbles = document.querySelectorAll(this.selectors.cartBubble);
    if (bubbles.length) {
      if (count > 0) {
        bubbles.forEach(b => {
          b.classList.add('cart-link__bubble--visible');
        });
      } else {
        bubbles.forEach(b => {
          b.classList.remove('cart-link__bubble--visible');
        });
      }
    }
  }
}
